import type {
  SwitchingBlocksEarliestLatest,
  SwitchingDetailed,
  SwitchingRead,
} from '@gridcon-energy/schaltungstool-backend'
import { SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import dayjs from 'dayjs/esm'
import type { AxiosResponse } from 'axios'
import type { BackendClients } from '~/api/backendClients'
import type { Paths } from 'type-fest'
import { get } from 'lodash-es'
import useI18nGlobal from '~/composables/common/useI18nGlobal'
import { formatDurationHours } from '~/helper/duration'

export type SwitchingExtended = SwitchingDetailed & {}
export type EarliestLatestDates = {
  earliest: dayjs.Dayjs
  latest: dayjs.Dayjs
}

export function formatDurationReleaseHours(value: unknown) {
  return typeof value === 'number'
    ? formatDurationHours(value)
    : useI18nGlobal().t('SwitchingsDurationReleaseHoursMissing')
}

/**
 * Helper function because often activated and deactivated have special logic
 */
export function isActivatedOrDeactivatedType(type: SwitchingCategoryTypeEnum) {
  return ActivatedDeactivatedSet.has(type as any)
}

export function isActivatedType(type: SwitchingCategoryTypeEnum) {
  return type === SwitchingCategoryTypeEnum.Activated
}

export function isDeactivatedType(type: SwitchingCategoryTypeEnum) {
  return type === SwitchingCategoryTypeEnum.Deactivated
}

const ActivatedDeactivatedSet = new Set([
  SwitchingCategoryTypeEnum.Activated,
  SwitchingCategoryTypeEnum.Deactivated,
])

/**
 * Status of a IBN/ABN switching
 */
export enum OfflineStatusEnum {
  pendingActivation = 'pendingActivation',
  deactivated = 'deactivated',
  temporary = 'temporary',
}

export function getOfflineStatus(switching: SwitchingDetailed): OfflineStatusEnum {
  if (switching.start_offline === null && switching.end_offline) {
    //  start missing
    return OfflineStatusEnum.pendingActivation
  } else if (switching.end_offline === null && switching.start_offline) {
    // end missing
    return OfflineStatusEnum.deactivated
  } else {
    // start/end set
    return OfflineStatusEnum.temporary
  }
}

export type SwitchingsRelatedMap = Map<number, SwitchingDetailed>

export function getMinStartAndMaxEnd(switching: SwitchingDetailed) {
  return {
    min_possible_start: switching.switching_block.start_earliest || switching.start,
    max_possible_end: switching.switching_block.end_latest || switching.end,
  }
}

/**
 * Calculates the earliest start and the latest date which a switching can be moved to.
 *
 * `comparisonGroup` also contains the switching itself.
 */
export function calcSwitchingDateLimits(switching: SwitchingRead, comparisonGroup: SwitchingDetailed[]) {
  const minDatesArray: number[] = []
  const maxDatesArray: number[] = []
  for (const compared of comparisonGroup) {
    const { min_possible_start, max_possible_end } = getMinStartAndMaxEnd(compared)
    const siblingMinStart = min_possible_start
    const siblingMaxEnd = max_possible_end
    minDatesArray.push(dayjs(compared.start).diff(siblingMinStart))
    maxDatesArray.push(dayjs(siblingMaxEnd).diff(compared.end))
  }

  const minStartDate = dayjs(switching.start)
    .subtract(Math.min(...minDatesArray))
    .format()
  const maxEndDate = dayjs(switching.end)
    .add(Math.min(...maxDatesArray))
    .format()

  return { minStartDate, maxEndDate }
}

/**
 * Default sorting order for switching categories
 */
export const SwitchingCategoryPriority: Record<SwitchingCategoryTypeEnum, number> = {
  disabled: 1,
  protected: 2,
  bypassMode: 3,
  deactivated: 4,
  activated: 5,
  meta: 6,
}

/**
 * Resolves earliest and latest switching dates for a given activity.
 */
export async function resolveEarliestLatestDates(
  backend: BackendClients,
  activityId: number,
): Promise<EarliestLatestDates | undefined> {
  const response = await backend.activity_switchings.switchingsEarliestLatest(activityId)
  const switchingsEarliestLatest = response.data
  if (switchingsEarliestLatest.switching_earliest.start && switchingsEarliestLatest.switching_latest.end) {
    // Use switchings
    return {
      earliest: dayjs(switchingsEarliestLatest.switching_earliest.start),
      latest: dayjs(switchingsEarliestLatest.switching_latest.end),
    }
  }

  // Fallback query switching blocks
  const fallback: SwitchingBlocksEarliestLatest = await backend.switchingBlocks
    .switchingBlocksEarliestLatest(activityId)
    .then((response) => response.data)
  if (fallback.switching_block_earliest?.start_earliest && fallback.switching_block_latest?.end_latest) {
    return {
      earliest: dayjs(fallback.switching_block_earliest.start_earliest),
      latest: dayjs(fallback.switching_block_latest.end_latest),
    }
  }
  return undefined
}
export const SwitchingEqualCheckPaths = [
  'start',
  'end',
  'switching_category_type',
  'duration_release_hours',
  'sub_project.name',
  'sub_project.project.name',
  'sub_project.project.grid_operator',
  'info_tbt.alternating',
  'info_tbt.continuous',
  'info_tbt.daily',
  'info_tbt.saturday',
  'info_tbt.sunday',
  'info_tbt.workday',
] satisfies Paths<SwitchingDetailed, { maxRecursionDepth: 3 }>[]

export function isEqualSwitching(a: SwitchingDetailed, b: SwitchingDetailed) {
  for (const path of SwitchingEqualCheckPaths) {
    const aValue = get(a, path)
    const bValue = get(b, path)
    if (aValue !== bValue) {
      return false
    }
  }
  return true
}

export type SwitchingEqualCheckResult = {
  path: (typeof SwitchingEqualCheckPaths)[number]
  error: string
}

export function isEqualSwitchingExplained(a: SwitchingDetailed, b: SwitchingDetailed): SwitchingEqualCheckResult[] {
  const errors: SwitchingEqualCheckResult[] = []
  for (const path of SwitchingEqualCheckPaths) {
    const aValue = get(a, path)
    const bValue = get(b, path)
    if (aValue !== bValue) {
      errors.push({
        path: path,
        error: `${aValue} != ${bValue}`,
      })
    }
  }
  return errors
}
export function sortAscending(a: SwitchingRead | undefined, b?: SwitchingRead) {
  if (!a && !b) {
    return 0
  } else if (a && b) {
    return new Date(a.start).getTime() - new Date(b.start).getTime()
  } else if (a && !b) {
    return -1
  } else {
    return 1
  }
}
